import { httpClient } from './http-client';

export interface ExpressStatusResponse {
  expressId: string;
  appointmentId: string;
  status: 'WAITING' | 'SEARCHING' | 'ACCEPTED' | 'ROOM_CREATED' | 'IN_PROGRESS' | 'FINISHED' | 'EXPIRED' | 'CANCELLED';
  position?: number;
  estimatedTime?: number;
  doctorId?: string;
  doctorName?: string;
  sessionId?: string;
  roomId?: string;
  roomUrl?: string;
  duration?: number;
}

export interface QueueStatus {
  waitingCount: number;
  inProgressCount: number;
  availableDoctors: number;
  averageWaitTime: number;
}

export interface TriageData {
  summary?: string;
  chiefComplaint?: string;
  symptomIntensity?: number;
  symptomDuration?: string;
  symptomDetail?: string;
  medicalHistory?: string;
  currentMedications?: string;
  redFlags?: string[];
  recommendedSpecialty?: string;
  riskClassification?: 'EMERGENCY' | 'HIGH' | 'MODERATE' | 'LOW';
}

export const expressApi = {
  startExpress: (data: {
    appointmentId: string;
    specialtyId?: string;
    chiefComplaint?: string;
  }): Promise<ExpressStatusResponse> =>
    httpClient.post<ExpressStatusResponse>('/express/start', data),

  startExpressWithTriage: (data: {
    appointmentId: string;
    specialtyId?: string;
    chiefComplaint?: string;
    triageData?: TriageData;
  }): Promise<ExpressStatusResponse> =>
    httpClient.post<ExpressStatusResponse>('/express/start-with-triage', data),

  getStatus: (expressId: string): Promise<ExpressStatusResponse> =>
    httpClient.get<ExpressStatusResponse>(`/express/status/${expressId}`),

  getStatusByAppointment: (appointmentId: string): Promise<ExpressStatusResponse> =>
    httpClient.get<ExpressStatusResponse>(`/express/status/appointment/${appointmentId}`),

  getQueueStatus: (): Promise<QueueStatus> =>
    httpClient.get<QueueStatus>('/express/queue/status'),

  getQueuePosition: (expressId: string): Promise<{ position: number; estimatedTime: number; doctorsOnline: number }> =>
    httpClient.get<{ position: number; estimatedTime: number; doctorsOnline: number }>(`/express/queue/${expressId}`),

  getDoctorsOnline: (): Promise<{
    count: number;
    specialties: { id: string; name: string; count: number }[];
    data?: Array<{
      id: string;
      name: string;
      specialty: string;
      status: string;
      crm?: string | null;
      email?: string | null;
      phone?: string | null;
      lastSeenAt?: string | null;
    }>;
  }> =>
    httpClient.get('/express/doctors-online'),

  getWaitingPatients: (): Promise<{ data: any[] }> =>
    httpClient.get('/express/queue/waiting'),

  getInProgressAppointments: (): Promise<{ data: any[] }> =>
    httpClient.get('/express/queue/in-progress'),

  getPricing: (): Promise<{ price: number; priceCents: number; formattedPrice: string }> =>
    httpClient.get<{ price: number; priceCents: number; formattedPrice: string }>('/express/pricing'),

  cancelExpress: (expressId: string, reason?: string): Promise<{ success: boolean }> =>
    httpClient.post<{ success: boolean }>('/express/cancel', { expressId, reason }),

  abortForEmergency: (data: { appointmentId: string; reason?: string }): Promise<{
    success: boolean;
    appointmentId: string;
    refunded: boolean;
    refundAmount: number;
    reason?: string;
    message: string;
  }> =>
    httpClient.post('/express/abort-emergency', data),
};
